import React, { useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ConfirmPopup } from 'primereact/confirmpopup';
import { Dropdown } from 'primereact/dropdown';
import { Password } from 'primereact/password';
import { useSelector } from 'react-redux';
import { selectUserData } from '@reduxSlices/user/userSlices';
import config from '@/config';
import './styles.scss';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Toast } from 'primereact/toast';

const { API_URL } = config;

const ModalUsers = ({ visible, hide, usersData, updateLoad, allCompany }) => {
  const isEditing = usersData && usersData?._id;
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const { role } = useSelector(selectUserData) ?? {};
  const UsersSchema = Yup.object().shape({
    ...(role === 'admin'
      ? {
          company: Yup.object().required('Виберіть підприємство'),
        }
      : {}),
    role: Yup.string().required('Виберіть роль'),
    login: Yup.string().min(7, 'Занадто коротке!').max(50, 'Занадто довге!').required("Обов'язкове поле"),
    ...(!isEditing
      ? {
          password: Yup.string()
            .min(6, 'Пароль повинен бути довжиною мінімум 6 символів')
            .required('Поле пароля є обов’язковим'),
        }
      : {}),
    name: Yup.string().min(2, 'Занадто коротке!').max(100, 'Занадто довге!'),
    tzd: Yup.string().min(2, 'Занадто коротке!').max(50, 'Занадто довге!').required("Обов'язкове поле"),
    tzd_id: Yup.string().min(2, 'Занадто коротке!').max(50, 'Занадто довге!').required("Обов'язкове поле"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/users`, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...values, _id: usersData?._id }),
      });

      if (!response.ok || response.status !== 200) {
        throw new Error('Server error!');
      }

      const data = await response.json();

      if (data?.code === 202) {
        throw new Error(`Користувач уже існує`);
      }

      resetForm();
      hide();
      setLoading(false);
      updateLoad();
    } catch (error) {
      setLoading(false);
      toast.current.show({ severity: 'error', summary: 'Error', detail: `${error}`, life: 5000 });
    }

    setSubmitting(false);
  };

  return (
    <div className='card flex justify-content-center'>
      <Dialog
        header={isEditing ? 'Редагувати дані користувача' : 'Додати користувача'}
        visible={visible}
        className='dialog-form'
        onHide={hide}
      >
        <Formik
          initialValues={{
            company: usersData?.company_id ? { _id: usersData?.company_id, name: usersData?.company_name } : null,
            role: usersData?.role,
            login: usersData?.login ?? '',
            password: '',
            name: usersData?.name ?? '',
            tzd: usersData?.tzd ?? '',
            tzd_id: usersData?.tzd_id ?? '',
          }}
          validationSchema={UsersSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue }) => (
            <Form>
              <div className='m-0 form'>
                {role === 'admin' && (
                  <div className='form__elem'>
                    <span className='p-float-label'>
                      <Field name='company' className={errors.company && touched.company ? 'p-invalid' : ''}>
                        {({ field }) => (
                          <Dropdown
                            value={field.value}
                            options={allCompany ?? []}
                            onChange={(e) => setFieldValue('company', e.value)}
                            optionLabel='name'
                          />
                        )}
                      </Field>
                      <label htmlFor='company'>Підриємство</label>
                    </span>
                    {errors.company && touched.company ? (
                      <small id='company-help' className='p-error'>
                        {errors.company}
                      </small>
                    ) : null}
                  </div>
                )}
                <div className='form__elem'>
                  <span className='p-float-label'>
                    <Field name='role' className={errors.role && touched.role ? 'p-invalid' : ''}>
                      {({ field }) => (
                        <Dropdown
                          value={field.value}
                          options={['manager', 'inspector', ...(role === 'admin' ? ['admin'] : [])]}
                          onChange={(e) => setFieldValue('role', e.value)}
                        />
                      )}
                    </Field>
                    <label htmlFor='role'>Роль</label>
                  </span>
                  {errors.role && touched.role ? (
                    <small id='role-help' className='p-error'>
                      {errors.role}
                    </small>
                  ) : null}
                </div>
                <div className='form__elem'>
                  <span className='p-float-label'>
                    <Field name='login' as={InputText} className={errors.login && touched.login ? 'p-invalid' : ''} />
                    <label htmlFor='login'>Логін</label>
                  </span>
                  {errors.login && touched.login ? (
                    <small id='login-help' className='p-error'>
                      {errors.login}
                    </small>
                  ) : null}
                </div>
                {!isEditing && (
                  <div className='form__elem'>
                    <span className='p-float-label'>
                      <Field name='password'>
                        {({ field }) => (
                          <Password
                            value={field.value}
                            onChange={(e) => setFieldValue('password', e.target.value)}
                            toggleMask
                          />
                        )}
                      </Field>
                      <label htmlFor='password'>Пароль</label>
                    </span>
                    {errors.password && touched.password ? (
                      <small id='password-help' className='p-error'>
                        {errors.password}
                      </small>
                    ) : null}
                  </div>
                )}
                <div className='form__elem'>
                  <span className='p-float-label'>
                    <Field name='tzd' as={InputText} className={errors.tzd && touched.tzd ? 'p-invalid' : ''} />
                    <label htmlFor='tzd'>Назва ТЗД</label>
                  </span>
                  {errors.tzd && touched.tzd ? (
                    <small id='tzd-help' className='p-error'>
                      {errors.tzd}
                    </small>
                  ) : null}
                </div>
                <div className='form__elem'>
                  <span className='p-float-label'>
                    <Field
                      name='tzd_id'
                      as={InputText}
                      className={errors.tzd_id && touched.tzd_id ? 'p-invalid' : ''}
                    />
                    <label htmlFor='tzd_id'>Індифікатор ТЗД</label>
                  </span>
                  {errors.tzd_id && touched.tzd_id ? (
                    <small id='tzd_id-help' className='p-error'>
                      {errors.tzd_id}
                    </small>
                  ) : null}
                </div>
                <div className='form__elem'>
                  <span className='p-float-label'>
                    <Field name='name' as={InputText} className={errors.name && touched.name ? 'p-invalid' : ''} />
                    <label htmlFor='name'>Ім'я</label>
                  </span>
                  {errors.name && touched.name ? (
                    <small id='name-help' className='p-error'>
                      {errors.name}
                    </small>
                  ) : null}
                </div>
                <Button
                  type='submit'
                  loading={loading}
                  className='form__button'
                  label={isEditing ? 'Зберегти' : 'Створити'}
                  icon='pi pi-check'
                />
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
      <Toast ref={toast} />
    </div>
  );
};

export default ModalUsers;

export const DeleteUsers = ({ visible, open, hide, usersData, updateLoad }) => {
  const toast = useRef(null);
  const buttonEl = useRef(null);

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/users`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ _id: usersData?._id }),
      });

      if (!response.ok || response.status !== 200) {
        throw new Error('Server error!');
      }

      toast.current.show({ severity: 'info', summary: 'Видалено', detail: 'Користувач успішно видалена.', life: 3000 });
      updateLoad();
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: `${error}`, life: 5000 });
    }
  };

  const accept = () => {
    handleDelete();
  };

  const reject = () => {
    toast.current.show({
      severity: 'warn',
      summary: 'Відміна',
      detail: 'Ви відмінили видалення користувача.',
      life: 3000,
    });
  };
  return (
    <div>
      <Toast ref={toast} />
      <ConfirmPopup
        target={buttonEl.current}
        visible={visible}
        onHide={hide}
        message='Ви дійсно хочете видалити користувача?'
        icon='pi pi-exclamation-triangle'
        accept={accept}
        reject={reject}
        acceptLabel='Так'
        rejectLabel='Ні'
      />

      <Button type='button' ref={buttonEl} icon='pi pi-trash' severity='warning' rounded onClick={open} />
    </div>
  );
};
